import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, AlertTriangle } from "lucide-react";

interface BulkWorkflowOperationsProps {
  open: boolean;
  onClose: () => void;
  selectedWorkflows: string[];
  onComplete: () => void;
}

export function BulkWorkflowOperations({
  open,
  onClose,
  selectedWorkflows,
  onComplete,
}: BulkWorkflowOperationsProps) {
  const { toast } = useToast();
  const [operation, setOperation] = useState<string>('');
  const [value, setValue] = useState<string>('');
  const [processing, setProcessing] = useState(false);

  async function handleExecute() {
    if (!operation) {
      toast({
        title: "Error",
        description: "Please select an operation",
        variant: "destructive",
      });
      return;
    }

    if (['activate', 'deactivate'].includes(operation) && !value) {
      toast({
        title: "Error",
        description: "Please select a value",
        variant: "destructive",
      });
      return;
    }

    setProcessing(true);
    try {
      let updateData: any = {};

      switch (operation) {
        case 'activate':
          updateData = { is_active: true };
          break;
        case 'deactivate':
          updateData = { is_active: false };
          break;
        case 'change_category':
          if (!value) throw new Error("Category is required");
          updateData = { category: value };
          break;
        case 'change_difficulty':
          if (!value) throw new Error("Difficulty is required");
          updateData = { difficulty: value };
          break;
        case 'add_tier_restriction':
          // This requires more complex logic
          for (const workflowId of selectedWorkflows) {
            const { data: workflow } = await supabase
              .from('workflows')
              .select('tier_restrictions')
              .eq('id', workflowId)
              .single();

            const currentRestrictions = workflow?.tier_restrictions || [];
            if (!currentRestrictions.includes(value)) {
              await supabase
                .from('workflows')
                .update({ tier_restrictions: [...currentRestrictions, value] })
                .eq('id', workflowId);
            }
          }
          
          toast({
            title: "Success",
            description: `Updated ${selectedWorkflows.length} workflows`,
          });
          onComplete();
          onClose();
          return;

        case 'remove_tier_restriction':
          for (const workflowId of selectedWorkflows) {
            const { data: workflow } = await supabase
              .from('workflows')
              .select('tier_restrictions')
              .eq('id', workflowId)
              .single();

            const currentRestrictions = workflow?.tier_restrictions || [];
            await supabase
              .from('workflows')
              .update({ tier_restrictions: currentRestrictions.filter((t: string) => t !== value) })
              .eq('id', workflowId);
          }
          
          toast({
            title: "Success",
            description: `Updated ${selectedWorkflows.length} workflows`,
          });
          onComplete();
          onClose();
          return;
      }

      // Execute update for simple operations
      const { error } = await supabase
        .from('workflows')
        .update(updateData)
        .in('id', selectedWorkflows);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Updated ${selectedWorkflows.length} workflows`,
      });

      onComplete();
      onClose();
    } catch (error: any) {
      console.error('Error executing bulk operation:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to execute bulk operation",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Bulk Operations</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              You are about to modify <strong>{selectedWorkflows.length}</strong> workflow{selectedWorkflows.length !== 1 ? 's' : ''}.
              This action cannot be undone.
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label>Operation</Label>
            <Select value={operation} onValueChange={setOperation}>
              <SelectTrigger>
                <SelectValue placeholder="Select operation" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="activate">Activate</SelectItem>
                <SelectItem value="deactivate">Deactivate</SelectItem>
                <SelectItem value="change_category">Change Category</SelectItem>
                <SelectItem value="change_difficulty">Change Difficulty</SelectItem>
                <SelectItem value="add_tier_restriction">Add Tier Restriction</SelectItem>
                <SelectItem value="remove_tier_restriction">Remove Tier Restriction</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {operation === 'change_category' && (
            <div className="space-y-2">
              <Label>New Category</Label>
              <Select value={value} onValueChange={setValue}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member_operations">Member Operations</SelectItem>
                  <SelectItem value="asset_management">Asset Management</SelectItem>
                  <SelectItem value="governance">Governance</SelectItem>
                  <SelectItem value="financial_operations">Financial Operations</SelectItem>
                  <SelectItem value="estate_planning">Estate Planning</SelectItem>
                  <SelectItem value="advanced_structures">Advanced Structures</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {operation === 'change_difficulty' && (
            <div className="space-y-2">
              <Label>New Difficulty</Label>
              <Select value={value} onValueChange={setValue}>
                <SelectTrigger>
                  <SelectValue placeholder="Select difficulty" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {(operation === 'add_tier_restriction' || operation === 'remove_tier_restriction') && (
            <div className="space-y-2">
              <Label>Tier</Label>
              <Select value={value} onValueChange={setValue}>
                <SelectTrigger>
                  <SelectValue placeholder="Select tier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="foundation">Foundation</SelectItem>
                  <SelectItem value="active_path">Active Path</SelectItem>
                  <SelectItem value="elite">Elite</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={processing}>
            Cancel
          </Button>
          <Button onClick={handleExecute} disabled={processing || !operation}>
            {processing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              'Execute'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
